import { Modal } from '@/components/admin/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageShell } from '@/components/layout/PageShell';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { AdminUsageStatsOverview } from '@innermate/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  Clock,
  Download,
  Info,
  Loader2,
  LogIn,
  LogOut,
  type LucideIcon,
  MessagesSquare,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

// ─── 날짜 유틸 (ChatLog 와 동일 로직) ────────────────────────────────────────

function toDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

type QuickPreset = 'today' | 'week' | 'lastWeek' | 'month';

function getQuickRange(preset: QuickPreset): { from: string; to: string } {
  const now = new Date();
  const today = toDateStr(now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'week' || preset === 'lastWeek') {
    const day = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() + (day === 0 ? -6 : 1 - day));
    if (preset === 'lastWeek') monday.setDate(monday.getDate() - 7);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: toDateStr(monday), to: toDateStr(sunday) };
  }
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: toDateStr(first), to: toDateStr(last) };
}

/** dateStr (YYYY-MM-DD) → ISO datetime (UTC 자정 기준). */
function dateToIso(s: string, endOfDay: boolean): string {
  return new Date(`${s}T${endOfDay ? '23:59:59.999Z' : '00:00:00.000Z'}`).toISOString();
}

// ─── 필터 상태 ───────────────────────────────────────────────────────────────

interface FilterState {
  from: string;
  to: string;
  userIds: string[];
  skillIds: string[];
  llmEndpointIds: string[];
}

function makeDefaultFilter(): FilterState {
  const { from, to } = getQuickRange('week');
  return { from, to, userIds: [], skillIds: [], llmEndpointIds: [] };
}

const QUICK_PRESETS: { label: string; preset: QuickPreset }[] = [
  { label: '오늘', preset: 'today' },
  { label: '이번 주', preset: 'week' },
  { label: '지난 주', preset: 'lastWeek' },
  { label: '이번 달', preset: 'month' },
];

// MultiSelectFilter 는 @/components/ui/multi-select-filter 에서 import.

// ─── 페이지 ──────────────────────────────────────────────────────────────────

export function AdminUsageStats() {
  // 단일 filters state — 변경 즉시 반영. 400ms debounced 가 실제 query 발화.
  const [filters, setFilters] = useState<FilterState>(makeDefaultFilter);
  const [debouncedFilters, setDebouncedFilters] = useState<FilterState>(filters);
  const [topModal, setTopModal] = useState<TopModalKind>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(t);
  }, [filters]);

  // 사용자 multi-select — 서버 limit/offset 페이지네이션 + search 위임 (ChatLog 와 동일 패턴).
  // 사용자 풀이 200 명을 넘는 환경에서 클라이언트 substring 필터로는 못 찾는 문제를 회피.
  const USER_PAGE_SIZE = 200;
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [selectedUserLabels, setSelectedUserLabels] = useState<Record<string, string>>({});
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch), 200);
    return () => clearTimeout(t);
  }, [userSearch]);
  const usersInfinite = useInfiniteQuery({
    queryKey: ['admin', 'users', 'infinite', debouncedUserSearch],
    queryFn: ({ pageParam }) =>
      api.adminUsersList({
        ...(debouncedUserSearch ? { search: debouncedUserSearch } : {}),
        limit: USER_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.users.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 60_000,
  });
  const skillsQ = useQuery({
    queryKey: ['admin', 'skills', 'all'],
    queryFn: () => api.adminSkillList(),
    staleTime: 60_000,
  });
  const llmQ = useQuery({
    queryKey: ['admin', 'llm', 'all'],
    queryFn: () => api.adminLlmList(),
    staleTime: 60_000,
  });
  const userOptions = useMemo(() => {
    const pages = usersInfinite.data?.pages ?? [];
    return pages.flatMap((p) =>
      p.users.map((u) => ({
        id: u.id,
        label: `${u.displayName ?? u.email} (${u.email})`,
      })),
    );
  }, [usersInfinite.data]);
  const selectedUserExtras = useMemo(
    () => Object.entries(selectedUserLabels).map(([id, label]) => ({ id, label })),
    [selectedUserLabels],
  );
  const handleUserSelectionChange = (ids: string[]) => {
    // 새로 선택된 ID 의 label 캐싱 → 페이지/검색 결과가 좁혀져도 트리거 라벨 보존.
    const additions = ids.filter((id) => !filters.userIds.includes(id));
    if (additions.length > 0) {
      setSelectedUserLabels((prev) => {
        const next = { ...prev };
        for (const id of additions) {
          const opt = userOptions.find((o) => o.id === id);
          if (opt && !next[id]) next[id] = opt.label;
        }
        return next;
      });
    }
    setFilters((f) => ({ ...f, userIds: ids }));
  };
  const skillOptions = useMemo(
    () => (skillsQ.data?.skills ?? []).map((s) => ({ id: s.id, label: s.name })),
    [skillsQ.data],
  );
  const llmOptions = useMemo(
    () =>
      (llmQ.data?.endpoints ?? []).map((e) => ({
        id: e.id,
        label: `${e.displayName ?? e.name}${e.model ? ` (${e.model})` : ''}`,
      })),
    [llmQ.data],
  );

  const queryArgs = useMemo(
    () => ({
      from: dateToIso(debouncedFilters.from, false),
      to: dateToIso(debouncedFilters.to, true),
      userIds: debouncedFilters.userIds.length > 0 ? debouncedFilters.userIds : undefined,
      skillIds: debouncedFilters.skillIds.length > 0 ? debouncedFilters.skillIds : undefined,
      llmEndpointIds:
        debouncedFilters.llmEndpointIds.length > 0 ? debouncedFilters.llmEndpointIds : undefined,
    }),
    [debouncedFilters],
  );

  const overviewQ = useQuery({
    queryKey: ['admin', 'usage-stats', 'overview', queryArgs],
    queryFn: () => api.adminGetUsageStatsOverview(queryArgs),
  });

  return (
    <PageShell
      header={
        <div className="flex flex-col gap-3">
          <PageHeader
            title="사용 통계"
            description="기간/사용자/스킬/LLM 별 채팅 사용량 · 오류 · 토큰 지표"
            actions={
              overviewQ.isFetching ? (
                <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
                  <Loader2 className="size-3.5 animate-spin" />
                  갱신 중…
                </div>
              ) : undefined
            }
          />
          <FilterRow
            filters={filters}
            onChange={(p) => setFilters((f) => ({ ...f, ...p }))}
            userOptions={userOptions}
            skillOptions={skillOptions}
            llmOptions={llmOptions}
            onUserChange={handleUserSelectionChange}
            onUserSearchChange={setUserSearch}
            onUserLoadMore={() => usersInfinite.fetchNextPage()}
            userHasMore={usersInfinite.hasNextPage}
            userIsLoadingMore={usersInfinite.isFetchingNextPage}
            selectedUserExtras={selectedUserExtras}
          />
        </div>
      }
    >
      {overviewQ.isLoading && (
        <div className="grid place-items-center py-12 text-sm text-foreground-muted">
          불러오는 중…
        </div>
      )}
      {overviewQ.isError && (
        <div className="grid place-items-center py-12 text-sm text-destructive">
          불러오기 실패: {String((overviewQ.error as Error).message)}
        </div>
      )}
      {overviewQ.data && (
        <div className="flex flex-col gap-6">
          <KpiRow data={overviewQ.data} />
          <DailyTrendChart data={overviewQ.data.daily} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TopList
              title="사용자 top 5"
              items={overviewQ.data.topUsers.map((u) => ({
                key: u.userId,
                label: u.displayName ?? u.email ?? u.userId,
                value: u.turnCount,
                secondaryValue: u.sessionCount,
              }))}
              primarySuffix="턴"
              secondarySuffix="세션"
              onShowMore={() => setTopModal('users')}
            />
            <TopList
              title="스킬 top 5"
              items={overviewQ.data.topSkills.map((s) => ({
                key: s.skillId ?? '__none__',
                label: s.name,
                value: s.turnCount,
                secondaryValue: s.sessionCount,
              }))}
              primarySuffix="턴"
              secondarySuffix="세션"
              onShowMore={() => setTopModal('skills')}
              info={{
                ariaLabel: '스킬 카운트 attribution 안내',
                text: '다중 스킬 세션은 활성화된 모든 스킬 그룹에 각각 카운트됩니다. 예: [DB분석가, 위키] 세션 → 두 그룹에 1씩 기여.',
              }}
            />
            <TopList
              title="도구 top 5"
              items={overviewQ.data.topTools.map((t) => ({
                key: t.name,
                label: t.name,
                value: t.callCount,
                secondaryValue: t.errorCount,
                secondaryDisplay: renderToolSecondary(t.errorCount, t.errorRate),
                errorRatio: t.errorRate,
              }))}
              primarySuffix="호출"
              secondarySuffix=""
              onShowMore={() => setTopModal('tools')}
              info={{
                ariaLabel: '도구 카운트 attribution 안내',
                text: '세션 단위 attribution — 필터 스킬이 포함된 세션의 모든 도구 호출을 집계해요. 다중 스킬 세션의 경우 다른 스킬에서 호출된 도구도 포함될 수 있어요.',
              }}
            />
          </div>
          <DailyBreakdownTable
            overview={overviewQ.data}
            range={{ from: debouncedFilters.from, to: debouncedFilters.to }}
          />
        </div>
      )}
      <TopFullListModal kind={topModal} queryArgs={queryArgs} onClose={() => setTopModal(null)} />
    </PageShell>
  );
}

// ─── 필터 행 (multi-select 3종 + 기간 필터) ─────────────────────────────────

function FilterRow({
  filters,
  onChange,
  userOptions,
  skillOptions,
  llmOptions,
  onUserChange,
  onUserSearchChange,
  onUserLoadMore,
  userHasMore,
  userIsLoadingMore,
  selectedUserExtras,
}: {
  filters: FilterState;
  onChange: (p: Partial<FilterState>) => void;
  userOptions: { id: string; label: string }[];
  skillOptions: { id: string; label: string }[];
  llmOptions: { id: string; label: string }[];
  onUserChange: (ids: string[]) => void;
  onUserSearchChange: (q: string) => void;
  onUserLoadMore: () => void;
  userHasMore: boolean;
  userIsLoadingMore: boolean;
  selectedUserExtras: { id: string; label: string }[];
}) {
  return (
    <div className="flex items-start justify-between gap-6 flex-wrap">
      {/* 왼쪽: 사용자 / 엔드포인트 / 스킬 multi-select */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-56">
          <MultiSelectFilter
            options={userOptions}
            selected={filters.userIds}
            onChange={onUserChange}
            placeholder="이름/이메일 검색"
            triggerPlaceholder="사용자 (전체)"
            onSearchChange={onUserSearchChange}
            onLoadMore={onUserLoadMore}
            hasMore={userHasMore}
            isLoadingMore={userIsLoadingMore}
            selectedExtras={selectedUserExtras}
          />
        </div>
        <div className="w-56">
          <MultiSelectFilter
            options={llmOptions}
            selected={filters.llmEndpointIds}
            onChange={(ids) => onChange({ llmEndpointIds: ids })}
            placeholder="이름/모델 검색"
            triggerPlaceholder="엔드포인트 (전체)"
          />
        </div>
        <div className="w-56">
          <MultiSelectFilter
            options={skillOptions}
            selected={filters.skillIds}
            onChange={(ids) => onChange({ skillIds: ids })}
            placeholder="스킬명 검색"
            triggerPlaceholder="스킬 (전체)"
          />
        </div>
      </div>

      {/* 오른쪽: 기간 + 빠른 범위 (chat-log 동일 스타일) */}
      <div className="flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-foreground-muted">기간</span>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => onChange({ from: e.target.value })}
            className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
          />
          <span className="text-xs text-foreground-muted">~</span>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => onChange({ to: e.target.value })}
            className="h-8 rounded-md border border-border bg-surface px-2 text-xs"
          />
        </div>
        <div className="flex items-center gap-1">
          {QUICK_PRESETS.map(({ label, preset }) => (
            <button
              key={preset}
              type="button"
              onClick={() => onChange(getQuickRange(preset))}
              className="h-6 rounded px-2 text-[11px] text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors cursor-pointer"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── KPI 카드 ───────────────────────────────────────────────────────────────

function KpiRow({ data }: { data: AdminUsageStatsOverview }) {
  const fmt = (n: number) => n.toLocaleString();
  const fmtAvg = (n: number) => (n >= 100 ? Math.round(n).toLocaleString() : n.toFixed(1));
  /** 1초/1분/1시간 경계로 단위 확장. p50/p95 가 매우 긴 케이스 (>1분) 도 자연스럽게 표기. */
  const fmtMs = (ms: number | null): string => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(2)}s`;
    if (ms < 3_600_000) return `${(ms / 60_000).toFixed(1)}m`;
    return `${(ms / 3_600_000).toFixed(1)}h`;
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <KpiCard
        label="사용자 수"
        value={fmt(data.userCount)}
        suffix="명"
        sub={`방문 수 ${fmt(data.visitCount)}회 (로그인 기준)`}
        icon={Users}
      />
      <KpiCard
        label="턴 수"
        value={fmt(data.turnCount)}
        suffix="건"
        sub={
          data.sessionCount > 0
            ? `세션 ${fmt(data.sessionCount)}개 · 세션당 ${fmtAvg(data.turnCount / data.sessionCount)}턴`
            : `세션 ${fmt(data.sessionCount)}개`
        }
        icon={MessagesSquare}
      />
      <KpiCard
        label="오류 건수"
        value={fmt(data.errorCount)}
        suffix="건"
        accent={data.errorCount > 0 ? 'destructive' : undefined}
        sub={
          data.errorCount === 0
            ? '오류 없음'
            : `도구 ${fmt(data.toolErrorCount)}건 · 시스템 ${fmt(data.systemErrorCount)}건`
        }
        icon={TriangleAlert}
      />
      <KpiCard
        label="응답 시간 (p50 기준)"
        value={fmtMs(data.responseTimeP50Ms)}
        sub={
          data.responseTimeP95Ms == null ? undefined : `p95 기준 ${fmtMs(data.responseTimeP95Ms)}`
        }
        icon={Clock}
      />
      <KpiCard
        label="입력 토큰"
        value={fmt(data.inputTokenSum)}
        sub={`평균 ${fmtAvg(data.inputTokenAvgPerTurn)} / 턴`}
        icon={LogIn}
      />
      <KpiCard
        label="출력 토큰"
        value={fmt(data.outputTokenSum)}
        sub={`평균 ${fmtAvg(data.outputTokenAvgPerTurn)} / 턴`}
        icon={LogOut}
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  suffix,
  sub,
  accent,
  icon: Icon,
}: {
  label: string;
  value: string;
  suffix?: string;
  sub?: string;
  accent?: 'destructive';
  icon?: LucideIcon;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-muted p-4 relative overflow-hidden">
      {Icon ? (
        <Icon
          aria-hidden
          className="absolute top-3 right-3 size-8 text-foreground-muted/25 pointer-events-none"
        />
      ) : null}
      <div className="text-xs text-foreground-muted mb-1.5">{label}</div>
      <div className="flex items-baseline gap-1">
        <span
          className={cn(
            'text-2xl font-semibold tabular-nums',
            accent === 'destructive' && 'text-destructive',
          )}
        >
          {value}
        </span>
        {suffix ? <span className="text-sm text-foreground-muted">{suffix}</span> : null}
      </div>
      {sub ? (
        <div className="text-[11px] text-foreground-muted mt-1.5 tabular-nums">{sub}</div>
      ) : null}
    </div>
  );
}

// ─── 일자별 추이 차트 (탭: 사용자 / 오류 / 토큰) ───────────────────────────

const AXIS_COLOR = 'hsl(var(--foreground-muted))';
const GRID_COLOR = 'hsl(var(--border-muted))';
const TOOLTIP_STYLE = {
  backgroundColor: 'hsl(var(--surface))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 6,
  fontSize: 12,
};
// 사용자 탭 시리즈 색상 — 사용자는 accent (hue 14, warm coral), 세션/턴은 보색 hue 194 (cyan-teal)
// 동일 hue 의 옅음/짙음 페어 (턴 수치가 더 크므로 짙게).
const USER_COLOR = 'hsl(var(--accent))';
const SESSION_COLOR = 'hsl(194 60% 65%)';
const TURN_COLOR = 'hsl(194 75% 42%)';

/** Y축 tick 축약 — `1234 → 1.2K`, `1500000 → 1.5M`. Tooltip 은 풀 자릿수 별도 (toLocaleString). */
const COMPACT_FORMATTER = new Intl.NumberFormat('en', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const fmtCompact = (n: number): string => COMPACT_FORMATTER.format(n);

type TrendTab = 'users' | 'errors' | 'tokens';

function DailyTrendChart({ data }: { data: AdminUsageStatsOverview['daily'] }) {
  const [tab, setTab] = useState<TrendTab>('users');

  if (data.length === 0) {
    return (
      <div className="rounded-md border border-border bg-surface-muted p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-medium">일자별 추이</h2>
          <TrendTabs tab={tab} onChange={setTab} />
        </div>
        <div className="grid place-items-center py-8 text-sm text-foreground-muted">
          이 기간 데이터 없음
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-border bg-surface-muted p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-medium">일자별 추이</h2>
          {tab === 'users' ? (
            // 차트 데이터 툴팁 (recharts) 과 동일한 surface 톤으로 맞춰 다크모드에서 튀지 않게 처리.
            // button 으로 감싸 키보드 포커스 가능 → group-focus-within 동작.
            <button
              type="button"
              aria-label="차트 데이터 기준 안내"
              className="group relative inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
            >
              <Info className="h-3.5 w-3.5 text-foreground-muted cursor-help" />
              <span
                role="tooltip"
                className={cn(
                  'pointer-events-none absolute z-50 top-full left-1/2 -translate-x-1/2 mt-1.5',
                  'inline-flex items-center whitespace-nowrap rounded-md',
                  'bg-surface text-foreground border border-border px-2 py-1 text-xs shadow-md',
                  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                  'transition-opacity duration-100 delay-150',
                )}
              >
                세션은 생성일, 사용자·턴은 메시지 발생일 기준
              </span>
            </button>
          ) : null}
        </div>
        <TrendTabs tab={tab} onChange={setTab} />
      </div>
      <div className="h-64 [&_*:focus]:outline-none [&_*:focus-visible]:outline-none">
        <ResponsiveContainer width="100%" height="100%">
          {tab === 'users' ? (
            <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
              <XAxis dataKey="date" stroke={AXIS_COLOR} tick={{ fontSize: 11 }} />
              {/* 듀얼 Y축: 좌측 = DAU (작은 스케일, bar), 우측 = 세션/턴 (큰 스케일, line) */}
              <YAxis
                yAxisId="dau"
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                tickFormatter={fmtCompact}
                width={56}
                label={{
                  value: '일일 사용자',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 11, fill: AXIS_COLOR, textAnchor: 'middle' },
                }}
              />
              <YAxis
                yAxisId="activity"
                orientation="right"
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                tickFormatter={fmtCompact}
                width={56}
                label={{
                  value: '세션 / 턴',
                  angle: 90,
                  position: 'insideRight',
                  style: { fontSize: 11, fill: AXIS_COLOR, textAnchor: 'middle' },
                }}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
                itemSorter={(item) => {
                  const order: Record<string, number> = { userCount: 0, sessions: 1, turns: 2 };
                  return order[String(item.dataKey)] ?? 99;
                }}
              />
              {/* ComposedChart 자동 범례는 Bar/Line 혼합 시 순서가 어긋나 custom content 로 고정. */}
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                content={() => (
                  <ul className="flex justify-center items-center gap-4 m-0 mt-1 p-0 list-none">
                    <li className="inline-flex items-center gap-1.5 text-foreground">
                      <span
                        style={{
                          display: 'inline-block',
                          width: 12,
                          height: 12,
                          background: USER_COLOR,
                          borderRadius: 2,
                          opacity: 0.7,
                        }}
                      />
                      일일 사용자
                    </li>
                    <li className="inline-flex items-center gap-1.5 text-foreground">
                      <span
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 2,
                          background: SESSION_COLOR,
                        }}
                      />
                      세션
                    </li>
                    <li className="inline-flex items-center gap-1.5 text-foreground">
                      <span
                        style={{
                          display: 'inline-block',
                          width: 14,
                          height: 2,
                          background: TURN_COLOR,
                        }}
                      />
                      턴
                    </li>
                  </ul>
                )}
              />
              <Bar
                yAxisId="dau"
                dataKey="userCount"
                fill={USER_COLOR}
                fillOpacity={0.7}
                name="일일 사용자"
                maxBarSize={32}
                radius={[2, 2, 0, 0]}
              />
              <Line
                yAxisId="activity"
                type="monotone"
                dataKey="sessions"
                stroke={SESSION_COLOR}
                strokeWidth={2}
                name="세션"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                yAxisId="activity"
                type="monotone"
                dataKey="turns"
                stroke={TURN_COLOR}
                strokeWidth={2}
                name="턴"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </ComposedChart>
          ) : tab === 'errors' ? (
            <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
              <XAxis dataKey="date" stroke={AXIS_COLOR} tick={{ fontSize: 11 }} />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                tickFormatter={fmtCompact}
                width={40}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="toolErrors"
                stackId="errors"
                stroke="hsl(15 90% 60%)"
                fill="hsl(15 90% 60%)"
                fillOpacity={0.35}
                strokeWidth={2}
                name="도구 오류"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Area
                type="monotone"
                dataKey="systemErrors"
                stackId="errors"
                stroke="hsl(0 72% 50%)"
                fill="hsl(0 72% 50%)"
                fillOpacity={0.35}
                strokeWidth={2}
                name="시스템 오류"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          ) : (
            <AreaChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid stroke={GRID_COLOR} strokeDasharray="2 3" />
              <XAxis dataKey="date" stroke={AXIS_COLOR} tick={{ fontSize: 11 }} />
              <YAxis
                stroke={AXIS_COLOR}
                tick={{ fontSize: 11 }}
                allowDecimals={false}
                tickFormatter={fmtCompact}
                width={48}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: 'hsl(var(--foreground))' }}
                formatter={(v) => (typeof v === 'number' ? v.toLocaleString() : String(v))}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="inputTokens"
                stackId="tokens"
                stroke="hsl(220 70% 55%)"
                fill="hsl(220 70% 55%)"
                fillOpacity={0.35}
                strokeWidth={2}
                name="입력 토큰"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Area
                type="monotone"
                dataKey="outputTokens"
                stackId="tokens"
                stroke="hsl(280 60% 60%)"
                fill="hsl(280 60% 60%)"
                fillOpacity={0.35}
                strokeWidth={2}
                name="출력 토큰"
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function TrendTabs({ tab, onChange }: { tab: TrendTab; onChange: (t: TrendTab) => void }) {
  const tabs: { key: TrendTab; label: string }[] = [
    { key: 'users', label: '사용자' },
    { key: 'errors', label: '오류' },
    { key: 'tokens', label: '토큰' },
  ];
  return (
    <div className="flex items-center gap-1">
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onChange(t.key)}
          className={cn(
            'h-7 px-2.5 rounded-md text-xs cursor-pointer transition-colors',
            tab === t.key
              ? 'bg-surface border border-border text-foreground'
              : 'text-foreground-muted hover:text-foreground hover:bg-surface/60',
          )}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ─── Top-N 가로 막대 ────────────────────────────────────────────────────────

/** 도구 카드/모달 보조 표기 — errorCount + errorRate. 10%+ 면 빨간 톤. */
function renderToolSecondary(errorCount: number, errorRate: number): ReactNode {
  if (errorCount === 0) {
    return <span className="text-foreground-muted/70">(오류 없음)</span>;
  }
  const pct = Math.round(errorRate * 100);
  const isHigh = errorRate >= 0.1;
  return (
    <span className={cn(isHigh ? 'text-destructive/90' : 'text-foreground-muted/70')}>
      ({errorCount.toLocaleString()} 건 오류, {pct}%)
    </span>
  );
}

interface TopItem {
  key: string;
  label: string;
  /** 주 정렬값 (turn / 호출 수). */
  value: number;
  /** 보조 표기값 (session / error 건수). */
  secondaryValue: number;
  /** 기본 표기를 override 하고 싶을 때 (예: 도구의 "n건 오류, X%"). 제공되면 secondarySuffix 무시. */
  secondaryDisplay?: ReactNode;
  /** 0~1. 제공되면 progress bar 가 success(accent) + error(destructive) stacked 로 렌더. */
  errorRatio?: number;
}

/** 공통 progress bar — errorRatio 가 있으면 stacked, 없으면 단색. */
function TopBar({ widthPct, errorRatio }: { widthPct: number; errorRatio?: number }) {
  if (errorRatio === undefined) {
    return (
      <div className="h-1.5 bg-foreground/10 rounded-sm">
        <div className="h-full bg-accent/70 rounded-sm" style={{ width: `${widthPct}%` }} />
      </div>
    );
  }
  const successPct = widthPct * (1 - errorRatio);
  const errorPct = widthPct * errorRatio;
  return (
    <div className="h-1.5 bg-foreground/10 rounded-sm overflow-hidden flex">
      <div className="h-full bg-accent/70" style={{ width: `${successPct}%` }} />
      <div className="h-full bg-destructive" style={{ width: `${errorPct}%` }} />
    </div>
  );
}

function TopList({
  title,
  items,
  primarySuffix,
  secondarySuffix,
  onShowMore,
  info,
}: {
  title: string;
  items: TopItem[];
  primarySuffix: string;
  secondarySuffix: string;
  onShowMore: () => void;
  /** 카드 attribution 한계 안내 — Info 아이콘으로 표시. */
  info?: { ariaLabel: string; text: ReactNode };
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <div className="rounded-md border border-border bg-surface-muted p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-medium">{title}</h2>
          {info ? (
            // button 으로 감싸 키보드 포커스 가능. tooltip 은 surface 톤, 카드 내부 아래로 펼쳐짐.
            <button
              type="button"
              aria-label={info.ariaLabel}
              className="group relative inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
            >
              <Info className="h-3.5 w-3.5 text-foreground-muted cursor-help" />
              <span
                role="tooltip"
                className={cn(
                  'pointer-events-none absolute z-50 top-full left-0 mt-1.5',
                  'inline-block w-64 rounded-md',
                  'bg-surface text-foreground border border-border px-2.5 py-1.5 text-xs font-normal leading-relaxed shadow-md',
                  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                  'transition-opacity duration-100 delay-150',
                )}
              >
                {info.text}
              </span>
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onShowMore}
          className="text-xs text-foreground-muted hover:text-foreground cursor-pointer"
        >
          자세히 보기
        </button>
      </div>
      {items.length === 0 ? (
        <div className="grid place-items-center py-8 text-sm text-foreground-muted">
          데이터 없음
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const widthPct = (item.value / max) * 100;
            return (
              <li key={item.key} className="text-xs">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="truncate">{item.label}</span>
                  <span className="tabular-nums text-foreground-muted shrink-0">
                    {item.value.toLocaleString()} {primarySuffix}
                    {item.secondaryDisplay !== undefined ? (
                      <span className="ml-1">{item.secondaryDisplay}</span>
                    ) : (
                      <span className="ml-1 text-foreground-muted/70">
                        ({item.secondaryValue.toLocaleString()} {secondarySuffix})
                      </span>
                    )}
                  </span>
                </div>
                <TopBar widthPct={widthPct} errorRatio={item.errorRatio} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Top-N 전체 모달 (offset 페이지네이션) ──────────────────────────────────

type TopModalKind = 'users' | 'skills' | 'tools' | null;

const MODAL_PAGE_SIZE = 10;

function TopFullListModal({
  kind,
  queryArgs,
  onClose,
}: {
  kind: TopModalKind;
  queryArgs: {
    from: string;
    to: string;
    userIds?: string[];
    skillIds?: string[];
    llmEndpointIds?: string[];
  };
  onClose: () => void;
}) {
  const open = kind != null;
  const [page, setPage] = useState(0);
  // 페이지가 바뀌어도 막대 스케일은 1위(global) 기준 고정 — 1페이지 응답에서 캡쳐.
  const [globalMax, setGlobalMax] = useState(1);

  const queryArgsKey = JSON.stringify(queryArgs);
  // kind 또는 필터 변경 시 페이지 / 막대 스케일 리셋 — deps 는 변경 감지용.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps 가 body 에서 직접 참조되지 않지만 트리거 용도로 필요
  useEffect(() => {
    setPage(0);
    setGlobalMax(1);
  }, [kind, queryArgsKey]);

  const q = useQuery({
    queryKey: ['admin', 'usage-stats', 'top-full', kind, queryArgs, page],
    queryFn: async () => {
      const offset = page * MODAL_PAGE_SIZE;
      if (kind === 'users') {
        return api.adminGetUsageStatsTopUsers({ ...queryArgs, offset, limit: MODAL_PAGE_SIZE });
      }
      if (kind === 'skills') {
        return api.adminGetUsageStatsTopSkills({ ...queryArgs, offset, limit: MODAL_PAGE_SIZE });
      }
      return api.adminGetUsageStatsTopTools({ ...queryArgs, offset, limit: MODAL_PAGE_SIZE });
    },
    enabled: open,
    // 페이지 변경 시에만 이전 데이터 유지 (smooth paging). kind 변경 시엔 폐기 — 다른 종류 컨텐츠 잔상 방지.
    placeholderData: (prev) => {
      if (!prev) return undefined;
      if (kind === 'users' && 'users' in prev) return prev;
      if (kind === 'skills' && 'skills' in prev) return prev;
      if (kind === 'tools' && 'tools' in prev) return prev;
      return undefined;
    },
  });

  const items: TopItem[] = useMemo(() => {
    if (!q.data) return [];
    if ('users' in q.data) {
      return q.data.users.map((u) => ({
        key: u.userId,
        label: u.displayName ?? u.email ?? u.userId,
        value: u.turnCount,
        secondaryValue: u.sessionCount,
      }));
    }
    if ('skills' in q.data) {
      return q.data.skills.map((s) => ({
        key: s.skillId ?? '__none__',
        label: s.name,
        value: s.turnCount,
        secondaryValue: s.sessionCount,
      }));
    }
    return q.data.tools.map((t) => ({
      key: t.name,
      label: t.name,
      value: t.callCount,
      secondaryValue: t.errorCount,
      secondaryDisplay: renderToolSecondary(t.errorCount, t.errorRate),
      errorRatio: t.errorRate,
    }));
  }, [q.data]);

  // 1페이지 응답이 들어왔을 때만 globalMax 갱신 (이후 페이지에선 같은 스케일 유지).
  useEffect(() => {
    if (page === 0 && items[0]) {
      setGlobalMax(items[0].value);
    }
  }, [page, items]);

  const total = q.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / MODAL_PAGE_SIZE));
  const titleByKind: Record<Exclude<TopModalKind, null>, string> = {
    users: `사용자 전체 (${total}명)`,
    skills: `스킬 전체 (${total}개)`,
    tools: `도구 전체 (${total}종)`,
  };
  const descByKind: Record<Exclude<TopModalKind, null>, string> = {
    users: '턴 수 기준 desc — 1위 기준 막대 폭',
    skills: '턴 수 기준 desc — 1위 기준 막대 폭',
    tools: '호출 수 기준 desc — 1위 기준 막대 폭',
  };
  const primarySuffix = kind === 'tools' ? '호출' : '턴';
  const max = Math.max(1, globalMax);
  const startRank = page * MODAL_PAGE_SIZE + 1;
  const isInitialLoading = q.isLoading && !q.data;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={kind ? titleByKind[kind] : ''}
      description={kind ? descByKind[kind] : undefined}
      size="lg"
    >
      <div className="flex flex-col gap-3">
        {/* 10개 행 기준 고정 높이 — 페이지 이동 시 모달 리사이즈 방지. */}
        <div className="min-h-85">
          {isInitialLoading ? (
            <div className="grid place-items-center h-85 text-sm text-foreground-muted">
              불러오는 중…
            </div>
          ) : q.isError ? (
            <div className="grid place-items-center h-85 text-sm text-destructive">
              불러오기 실패
            </div>
          ) : items.length === 0 ? (
            <div className="grid place-items-center h-85 text-sm text-foreground-muted">
              데이터 없음
            </div>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((item, i) => {
                const widthPct = (item.value / max) * 100;
                return (
                  <li key={item.key} className="text-xs">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="tabular-nums text-foreground-muted shrink-0 w-8 text-right">
                        {startRank + i}.
                      </span>
                      <span className="truncate flex-1">{item.label}</span>
                      <span className="tabular-nums text-foreground-muted shrink-0">
                        {item.value.toLocaleString()} {primarySuffix}
                        {item.secondaryDisplay !== undefined ? (
                          <span className="ml-1">{item.secondaryDisplay}</span>
                        ) : (
                          <span className="ml-1 text-foreground-muted/70">
                            ({item.secondaryValue.toLocaleString()} 세션)
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="ml-10">
                      <TopBar widthPct={widthPct} errorRatio={item.errorRatio} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {/* 페이지네이션 — 데이터가 있을 때만 노출. */}
        {total > 0 && (
          <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || q.isFetching}
              className="h-7 px-3 rounded-md border border-border text-xs hover:bg-surface-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              이전
            </button>
            <span className="text-xs text-foreground-muted tabular-nums">
              {page + 1} / {totalPages} 페이지
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1 || q.isFetching}
              className="h-7 px-3 rounded-md border border-border text-xs hover:bg-surface-muted cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── 일자별 상세 테이블 (KPI 컬럼 분해 + CSV 다운로드) ────────────────────────

const CSV_HEADERS = [
  '날짜',
  '사용자',
  '방문',
  '세션',
  '턴',
  '도구 오류',
  '시스템 오류',
  '오류 합계',
  '입력 토큰',
  '출력 토큰',
] as const;

/** CSV 셀 이스케이프 — 쉼표/따옴표/개행 포함 시 quote-wrap. */
function csvCell(v: string | number): string {
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * 일자별 상세 CSV 생성·다운로드. BOM(UTF-8) prefix 로 Excel 한글 자동 인식.
 * 마지막 행은 daily 합이 아니라 KPI 값 — distinct count(사용자) 가 단순 SUM 무의미한 점 + 날짜 클램프
 * 외 영역까지 포함한 "기간 진짜 총합" 을 그대로 노출.
 */
function exportDailyCsv(overview: AdminUsageStatsOverview, range: { from: string; to: string }) {
  const dataRows = overview.daily.map((d) => [
    d.date,
    d.userCount,
    d.visitCount,
    d.sessions,
    d.turns,
    d.toolErrors,
    d.systemErrors,
    d.toolErrors + d.systemErrors,
    d.inputTokens,
    d.outputTokens,
  ]);
  const totalRow = [
    '[기간 합계]',
    overview.userCount,
    overview.visitCount,
    overview.sessionCount,
    overview.turnCount,
    overview.toolErrorCount,
    overview.systemErrorCount,
    overview.errorCount,
    overview.inputTokenSum,
    overview.outputTokenSum,
  ];
  const csv = `﻿${[CSV_HEADERS, ...dataRows, totalRow]
    .map((row) => row.map(csvCell).join(','))
    .join('\n')}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `innermate 사용 통계 (${range.from} ~ ${range.to}).csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function DailyBreakdownTable({
  overview,
  range,
}: {
  overview: AdminUsageStatsOverview;
  range: { from: string; to: string };
}) {
  const daily = overview.daily;
  const errClass = (n: number) => (n > 0 ? 'text-destructive/90' : 'text-foreground-muted/60');
  return (
    <div className="rounded-md border border-border bg-surface p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h2 className="text-sm font-medium">
          일자별 상세 <span className="text-foreground-muted ml-1">{daily.length}일</span>
        </h2>
        <button
          type="button"
          onClick={() => exportDailyCsv(overview, range)}
          disabled={daily.length === 0}
          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-border text-xs hover:bg-surface-muted cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="size-3.5" />
          다운로드
        </button>
      </div>
      <div className="overflow-x-auto border border-border rounded-md">
        <table className="w-full text-xs tabular-nums">
          <thead className="bg-surface-muted border-b border-border">
            <tr className="text-left text-foreground-muted">
              <th className="px-3 py-2">날짜</th>
              <th className="px-3 py-2 text-right">사용자</th>
              <th className="px-3 py-2 text-right">방문</th>
              <th className="px-3 py-2 text-right">세션</th>
              <th className="px-3 py-2 text-right">턴</th>
              <th className="px-3 py-2 text-right">도구 오류</th>
              <th className="px-3 py-2 text-right">시스템 오류</th>
              <th className="px-3 py-2 text-right">오류 합계</th>
              <th className="px-3 py-2 text-right">입력 토큰</th>
              <th className="px-3 py-2 text-right">출력 토큰</th>
            </tr>
          </thead>
          <tbody>
            {daily.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-8 text-center text-foreground-muted">
                  데이터 없음
                </td>
              </tr>
            ) : (
              <>
                {daily.map((d) => {
                  const errSum = d.toolErrors + d.systemErrors;
                  return (
                    <tr
                      key={d.date}
                      className="border-b border-border/50 hover:bg-surface-muted/40"
                    >
                      <td className="px-3 py-2 text-foreground-muted">{d.date}</td>
                      <td className="px-3 py-2 text-right">{d.userCount.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{d.visitCount.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{d.sessions.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{d.turns.toLocaleString()}</td>
                      <td className={cn('px-3 py-2 text-right', errClass(d.toolErrors))}>
                        {d.toolErrors.toLocaleString()}
                      </td>
                      <td className={cn('px-3 py-2 text-right', errClass(d.systemErrors))}>
                        {d.systemErrors.toLocaleString()}
                      </td>
                      <td className={cn('px-3 py-2 text-right', errClass(errSum))}>
                        {errSum.toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right">{d.inputTokens.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">{d.outputTokens.toLocaleString()}</td>
                    </tr>
                  );
                })}
                {/* 기간 합계 행 — daily SUM 이 아니라 KPI 값 (distinct count + 클램프 외 영역 포함) */}
                <tr className="bg-surface-muted border-t-2 border-border font-medium">
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5">
                      기간 합계
                      {/* 차트 Info 와 동일 surface 톤 — 다크모드에서 튀지 않게. multi-line 허용. */}
                      {/* button 으로 감싸 키보드 포커스 가능 → group-focus-within 동작. */}
                      <button
                        type="button"
                        aria-label="기간 합계 산정 안내"
                        className="group relative inline-flex rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/40"
                      >
                        <Info className="h-3.5 w-3.5 text-foreground-muted cursor-help" />
                        <span
                          role="tooltip"
                          className={cn(
                            'pointer-events-none absolute z-50 bottom-full left-0 mb-1.5',
                            'inline-block w-72 rounded-md',
                            'bg-surface text-foreground border border-border px-2.5 py-1.5 text-xs font-normal leading-relaxed shadow-md',
                            'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100',
                            'transition-opacity duration-100 delay-150',
                          )}
                        >
                          일별 합산이 아닌 KPI 카드 기준값이에요. 사용자 수는 중복 제외(distinct),
                          기타 지표는 필터 기간 외 활동도 포함될 수 있어 일별 합과 다를 수 있어요.
                        </span>
                      </button>
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{overview.userCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{overview.visitCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{overview.sessionCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right">{overview.turnCount.toLocaleString()}</td>
                  <td className={cn('px-3 py-2 text-right', errClass(overview.toolErrorCount))}>
                    {overview.toolErrorCount.toLocaleString()}
                  </td>
                  <td className={cn('px-3 py-2 text-right', errClass(overview.systemErrorCount))}>
                    {overview.systemErrorCount.toLocaleString()}
                  </td>
                  <td className={cn('px-3 py-2 text-right', errClass(overview.errorCount))}>
                    {overview.errorCount.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {overview.inputTokenSum.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {overview.outputTokenSum.toLocaleString()}
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
